/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/no-array-index-key */

import React from 'react';
import styled from 'styled-components';

import Typography from '@ntuctech/devex-tangram/Typography';
import SvgAlert from '@ntuctech/devex-tangram/Icon/SvgAlert';
import Link from '@ntuctech/devex-tangram/Link';

import { useAuth0 } from '../../Auth0Provider';
import { useFlowControl } from '../../FlowControlProvider';

import {
  SEPARATOR,
  STR_SEARCH_KEY,
  SUPPORT_LINK_NAME,
  SUPPORT_LINK_URL,
  SUPPORT_LINK_REPLACE_TEXT,
  PASSWORD_RESET_LINK_NAME,
  REVERIFY_OTP_LINK_NAME,
  REVERIFY_OTP_SCREEN_VERIFY_LINK_NAME,
  FLOW_STEPS,
} from '../../../constants';

import ParseError from '../../../services/parse-error';

const defaultStyle = `
  display: flex;
  align-items: center;

  border-radius: 0.5rem;

  animation-name: displayError;
  animation-duration: 1s;
  transition: all 0.3s linear;

  @keyframes displayError {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  & p {
    display: inline-block !important;
  }
`;

const spacing = `
  margin: 1rem 0;
  padding: 0.75rem;

  & svg {
    flex: 0 0 1.5rem;
  }
`;

const ErrorSC = styled.div`
  ${defaultStyle}
  ${spacing}

  background: ${(props) => props.theme.ColorBackgroundErrorLight3};

  & .SSOError {
    color: ${(props) => props.theme.ColorFontError};
  }
  & .SSOError__icon {
    fill: ${(props) => props.theme.ColorFontError};
    margin-right: 0.5rem;
  }
  & a:link,
  & a:visited,
  & a:hover,
  & a:active {
    color: ${(props) => props.theme.ColorFontError};
    font-weight: normal;
    box-shadow: 0 0.0625rem ${(props) => props.theme.ColorFontError};
  }
`;

const InfoSC = styled.div`
  ${defaultStyle}
  ${spacing}
  padding: 0.5rem 0.75rem;
  background: ${(props) => props.theme.ColorBackgroundPrimaryLight3};
`;

const isInfo = (errorMessage) => errorMessage.indexOf(SUPPORT_LINK_REPLACE_TEXT) >= 0;

/* this is a common component
and the outer context provider might vary
therefore we need to check for undefined
before use */
const ErrorBanner = ({ error, onLinkClick }) => {
  const { trackPwl } = useAuth0();
  const flowControlClient = useFlowControl();

  const { currentStep } = flowControlClient || {};

  const errorMessage = ParseError.parse(error, currentStep);

  const goToForgotPassword = (e) => {
    e.preventDefault();

    trackPwl({
      screen_name: 'SignIn - Email',
      event_action: 'Click',
      event_label: 'SignIn_Passwd_Error_Forgot',
    });

    if (flowControlClient !== undefined) {
      flowControlClient.goNext(FLOW_STEPS.PASSWORD_RESET_LINK);
    }
  };

  const goToReverifyPhoneNumber = (e) => {
    e.preventDefault();
    if (flowControlClient) {
      flowControlClient.reStartFromVerifyPhoneNumber();
    }
  };

  const attachNextStepLink = (message) => {
    const errorAsArray = message.split(SEPARATOR);

    if (errorAsArray.length === 1) {
      return message;
    }

    return errorAsArray.map((str, _i) => {
      if (str === STR_SEARCH_KEY.SUPPORT_LINK) {
        return (
          <Link
            key={`link_${_i}`}
            href={SUPPORT_LINK_URL}
            target="_blank"
            data-testid="ssoErrorSupportLink"
          >
            {SUPPORT_LINK_NAME}
          </Link>
        );
      }
      if (str === STR_SEARCH_KEY.PASSWORD_RESET_LINK) {
        return (
          <Link
            key={`link_${_i}`}
            href="#"
            onClick={goToForgotPassword}
            data-testid="ssoErrorForgotLink"
          >
            {PASSWORD_RESET_LINK_NAME}
          </Link>
        );
      }

      if (str === STR_SEARCH_KEY.REVERIFY_OTP_LINK) {
        return (
          <Link
            key={`link_${_i}`}
            href="#"
            onClick={goToReverifyPhoneNumber}
            data-testid="ssoErrorReverifyOTPLink"
          >
            {REVERIFY_OTP_LINK_NAME}
          </Link>
        );
      }

      if (str === STR_SEARCH_KEY.REVERIFY_OTP_LINK_SCREEN_VERIFY) {
        return (
          <Link
            key={`link_${_i}`}
            href="#"
            onClick={onLinkClick}
            data-testid="ssoErrorReverifyOTPScreenVerifyLink"
          >
            {REVERIFY_OTP_SCREEN_VERIFY_LINK_NAME}
          </Link>
        );
      }
      return str;
    });
  };

  const infoOrError = React.useMemo(() => isInfo(errorMessage), [errorMessage]);
  const ErrorBannerComp = infoOrError ? InfoSC : ErrorSC;

  return (
    <ErrorBannerComp data-testid={infoOrError ? 'ssoErrorInfo' : 'ssoErrorWarning'}>
      {!infoOrError && <SvgAlert size="s" className="SSOError__icon" />}
      <Typography variant="body3" className={infoOrError ? 'SSOInfo' : 'SSOError'}>
        {attachNextStepLink(errorMessage)}
      </Typography>
    </ErrorBannerComp>
  );
};

export default ErrorBanner;
